import React, { useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import {
  OriginThemeProvider,
  TrackingProvider,
} from "@origin-digital/ods-core";
import { QueryClient, QueryClientProvider } from "@origin-digital/query";
import { FeatureFlagContextProvider } from "@origin-digital/feature-flags";
import { trackingProviderCaptureClick } from "@origin-digital/reporting-client";
import { AppContextProvider } from "./context/AppContext";
import { ConvoSearchProvider } from "./context/ConvoSearchContext";
import { AppRoutes } from "./routes";
import { CustomLink } from "./components/CustomLink";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { config } from "./config";
import { GlobalStyles } from "./global-styles";

// Create QueryClient instance (no caching - staleTime: 0)
const queryClient = new QueryClient();
const EMPTY_FLAGS = {};

export const App: React.FunctionComponent<Record<string, unknown>> = React.memo(
  () => (
    <BrowserRouter>
      <ScrollToTop />
      <QueryClientProvider client={queryClient}>
        <OriginThemeProvider linkComponent={CustomLink} enableThemeSwitching>
          <FeatureFlagContextProvider
            featureFlags={config.featureFlags ?? EMPTY_FLAGS}
          >
            <AppContextProvider>
              <TrackingProvider
                onTrackingCapture={trackingProviderCaptureClick}
              >
                <ConvoSearchProvider>
                  <LocationAwareErrorBoundary>
                    <AppRoutes />
                  </LocationAwareErrorBoundary>
                </ConvoSearchProvider>
              </TrackingProvider>
            </AppContextProvider>
          </FeatureFlagContextProvider>
        </OriginThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
);

const LocationAwareErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};
